/**
 * THE ROOM'S INTERNET SEARCH (MODES.md §10.9) — the first row of the room's Connectors: "Internet search" ASSIGNED by
 * default (every room), visible to everyone, removable by the room's run roles — and addable back. One switch for the
 * whole room: Ask and Do (and builder runs) use the web only while it is assigned.
 *
 *   <RoomInternetSearch value={{ on, isDefault, setByName, canSet }} onChange={(on) => …} />
 */
import React, { useState } from 'react';
import { Globe, X, Plus, Loader2 } from 'lucide-react';

export default function RoomInternetSearch({ value = {}, onChange = null }) {
  const on = value.on !== false;
  const canSet = !!value.canSet && typeof onChange === 'function';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const flip = async (next) => {
    setBusy(true); setError('');
    try { await onChange(next); } catch (e) { setError((e && e.message) || "Couldn't change it — try again."); }
    setBusy(false);
  };
  const who = value.isDefault ? 'On by default for every room'
    : value.setByName ? `${on ? 'Added' : 'Removed'} by ${value.setByName}` : '';
  return (
    <div data-testid="room-internet-search" data-on={on ? 'true' : 'false'}
      className={`mb-3 flex items-center gap-2 rounded-lg border px-3 py-2 ${on ? 'border-indigo-200 bg-indigo-50/60' : 'border-dashed border-gray-300 bg-white'}`}>
      <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${on ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
        <Globe className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-900">
          Internet search
          <span className={`rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${on ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
            {on ? 'Room wide' : 'Off'}
          </span>
        </div>
        <div className="truncate text-[11px] text-gray-500">
          {on ? 'Agents may search the web and read pages when they Ask or Do — answers show their sources.'
            : 'Agents work only from the room’s context and files — no web.'}
          {who ? <span className="ml-1 text-gray-400">· {who}</span> : null}
        </div>
        {error ? <div className="text-[11px] text-rose-600">{error}</div> : null}
      </div>
      {canSet ? (
        on ? (
          <button type="button" data-testid="room-internet-search-remove" onClick={() => flip(false)} disabled={busy}
            title="Remove Internet search for everyone in this room"
            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-white hover:text-rose-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <X className="h-3.5 w-3.5" aria-hidden />}Remove
          </button>
        ) : (
          <button type="button" data-testid="room-internet-search-add" onClick={() => flip(true)} disabled={busy}
            title="Let this room's agents search the web again"
            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Plus className="h-3.5 w-3.5" aria-hidden />}Add
          </button>
        )
      ) : null}
    </div>
  );
}
